/**
 * M2 onboarding: Information section — Description, Website URL.
 *
 * Display logic:
 *   - If a field was pre-filled by the EIN lookup or website enrichment API
 *     (i.e. `payload` contains a truthy value), show the read-only text with a
 *     pencil icon that toggles into an editable input.
 *   - If the field was NOT pre-filled, render a regular editable input directly.
 */

import React, { useState } from 'react';
import { INPUT_CLASS, TEXTAREA_CLASS } from './orgSetupUtils';

const DESCRIPTION_MAX = 1100;
const DESCRIPTION_PREVIEW_LIMIT = 300;

const PencilButton = ({ onClick, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    className="shrink-0 p-1 rounded text-ink-muted hover:text-ink hover:bg-paper-muted"
    aria-label={ariaLabel}
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  </button>
);

export default function OrgInformation({
  payload,
  description,
  descriptionLoading = false,
  onDescriptionChange,
  websiteUrl,
  onWebsiteUrlChange,
}) {
  const [editingField, setEditingField] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // Determine if each field was pre-populated by the API (EIN lookup or website enrich)
  const descriptionPrefilled = !!(payload?.description || payload?.ogDescription);
  const websitePrefilled = !!(payload?.websiteUrl || payload?.website);

  const displayWebsite = websiteUrl ?? payload?.websiteUrl ?? '';
  const displayDescription = description ?? payload?.description ?? '';

  return (
    <div className="space-y-6">
      {onDescriptionChange && (
        <div className="space-y-1.5 ">
          <div className="flex items-center justify-between gap-2">
            <label className="text-base font-medium text-ink-secondary">Description</label>
            {/* Only show pencil toggle when the field was API-prefilled */}
            {descriptionPrefilled && (
              <PencilButton
                onClick={() => setEditingField(editingField === 'description' ? null : 'description')}
                ariaLabel="Edit description"
              />
            )}
          </div>
          <p className="text-xs italic text-ink-muted">What does your organization do? What are you fundraising for?</p>

          {descriptionLoading && !displayDescription?.trim() ? (
            <div
              className="rounded-md border border-sage/25 bg-sage-light/40 px-4 py-3"
              aria-busy="true"
              aria-live="polite"
            >
              <p className="mb-3 text-sm font-medium text-ink-secondary">Generating description</p>
              <div className="animate-pulse space-y-2 rounded-md border border-edge/50 bg-paper px-3 py-2.5">
                <div className="h-2.5 w-4/5 rounded bg-sage/20" />
                <div className="h-2.5 w-full rounded bg-sage/20" />
                <div className="h-2.5 w-11/12 rounded bg-sage/20" />
              </div>
            </div>
          ) : descriptionPrefilled && editingField !== 'description' ? (
            // API-prefilled: show read-only text with expand/collapse if long
            <div>
              <p className="text-sm text-ink whitespace-pre-wrap">
                {descriptionExpanded || displayDescription.length <= DESCRIPTION_PREVIEW_LIMIT
                  ? (displayDescription || '—')
                  : displayDescription.slice(0, DESCRIPTION_PREVIEW_LIMIT) + '…'}
              </p>
              {displayDescription.length > DESCRIPTION_PREVIEW_LIMIT && (
                <button
                  type="button"
                  onClick={() => setDescriptionExpanded((v) => !v)}
                  className="mt-1 text-xs font-medium text-sage hover:text-sage-hover"
                >
                  {descriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          ) : (
            // Not prefilled OR user clicked pencil: show editable textarea with char counter
            <div>
              <textarea
                value={displayDescription}
                onChange={(e) => onDescriptionChange(e.target.value.slice(0, DESCRIPTION_MAX))}
                rows={4}
                maxLength={DESCRIPTION_MAX}
                className={TEXTAREA_CLASS}
                placeholder="The more you share, the better we can customize your dashboard experience."
                autoFocus={editingField === 'description'}
              />
            </div>
          )}
        </div>
      )}

      {onWebsiteUrlChange && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <label className="text-base font-medium text-ink-secondary">Website URL</label>
            {/* Only show pencil toggle when the field was API-prefilled */}
            {websitePrefilled && (
              <PencilButton
                onClick={() => setEditingField(editingField === 'websiteUrl' ? null : 'websiteUrl')}
                ariaLabel="Edit website URL"
              />
            )}
          </div>

          {websitePrefilled && editingField !== 'websiteUrl' ? (
            // API-prefilled: show read-only link (pencil toggles to edit)
            <p className="text-base text-ink">{displayWebsite ? (
              <a href={displayWebsite.startsWith('http') ? displayWebsite : `https://${displayWebsite}`} target="_blank" rel="noopener noreferrer" className="text-sage hover:underline">{displayWebsite}</a>
            ) : '—'}</p>
          ) : (
            // Not prefilled OR user clicked pencil: show regular input
            <input
              type="url"
              value={displayWebsite}
              onChange={(e) => onWebsiteUrlChange(e.target.value)}
              placeholder="https://..."
              className={INPUT_CLASS}
              autoFocus={editingField === 'websiteUrl'}
            />
          )}
        </div>
      )}
    </div>
  );
}
